import { useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useProvinces, useDistricts } from '../../lib/geo'
import { SYSTEM_ROLES } from '../../lib/systemRoles'
import type { Organization, UserProfile } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, Input, PasswordInput, Select,
} from '../../components/ui'
import { Wizard } from '../../components/Wizard'

/* Single page combining the four Jinja create-user forms
 * (admin/{create_gov_user,create_leader,create_miner,create_staff}.html)
 * behind one role-type selector, submitting to their JSON counterparts
 * (POST /api/v1/admin/users/create-{gov,leader,miner,staff}). Each Jinja
 * original was its own 2-step Alpine wizard (Account Details -> Role &
 * Assignment); this port flattens each into one scrollable form per tab
 * instead of nesting a second wizard inside the role-type selector - the
 * fields are identical, only the step chrome is dropped. */

type RoleType = 'officer' | 'leader' | 'miner' | 'staff'

const TABS: { key: RoleType; label: string }[] = [
  { key: 'officer', label: 'Government Official' },
  { key: 'leader', label: 'Team Leader' },
  { key: 'miner', label: 'Miner' },
  { key: 'staff', label: 'Staff' },
]

const TAB_HINTS: Record<RoleType, string> = {
  officer: 'Create a government official account directly. They can log in immediately and reset their password later.',
  leader: "Create a Team Leader account directly. They'll manage a mining organisation's members and license applications.",
  miner: 'Create a miner account directly. The miner will be able to log in immediately.',
  staff: 'Create a user and assign any role - including Safety Officer, Site Manager, or any custom role defined in the Role Builder.',
}

interface CreateResult { ok: boolean; item: UserProfile }

function isRoleType(v: string | null): v is RoleType {
  return v === 'officer' || v === 'leader' || v === 'miner' || v === 'staff'
}

export default function AdminCreateUser() {
  // admin/Users.tsx's "+ Create Officer/Leader/Miner/Staff" buttons all link
  // to this same /users/new route (matching the "one page, one selector"
  // design here) - reading an optional ?type= preselects the right tab for
  // whichever button was clicked, if the caller sends one. Falls back to the
  // 'officer' default this page always had when it's absent or invalid.
  const [searchParams] = useSearchParams()
  const requestedType = searchParams.get('type')
  const [roleType, setRoleType] = useState<RoleType>(isRoleType(requestedType) ? requestedType : 'officer')
  const navigate = useNavigate()
  const provinces = useProvinces()

  // Only the leader/miner/staff tabs need the org picker.
  const orgsQuery = useQuery({
    queryKey: ['admin', 'orgs', 'all'],
    queryFn: () => api.get<{ ok: boolean; items: Organization[] }>('/admin/orgs?status=&limit=200'),
    enabled: roleType !== 'officer',
  })
  const orgs = orgsQuery.data?.items ?? []

  function onCreated() { navigate('/users') }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Create user</h1>
          <p className="page-head__sub">Create an account directly, without going through self-signup.</p>
        </div>
      </div>

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        {TABS.map((t) => (
          <Button
            key={t.key} size="sm"
            variant={roleType === t.key ? 'primary' : 'secondary'}
            onClick={() => setRoleType(t.key)}
          >
            {t.label}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader title={TABS.find((t) => t.key === roleType)?.label} sub={TAB_HINTS[roleType]} />
        <CardBody>
          {roleType === 'officer' && <OfficerForm provinces={provinces} onCreated={onCreated} />}
          {roleType === 'leader' && <LeaderForm provinces={provinces} orgs={orgs} onCreated={onCreated} />}
          {roleType === 'miner' && <MinerForm provinces={provinces} orgs={orgs} onCreated={onCreated} />}
          {roleType === 'staff' && <StaffForm provinces={provinces} orgs={orgs} onCreated={onCreated} />}
        </CardBody>
      </Card>
    </>
  )
}

/* ── Shared bits ─────────────────────────────────────────────────────── */

type Province = { id: string; name: string }

/* Contact-only (3 fields) - the NRC moved to its own demographic step via
 * NrcField below, honouring the wizard rules: at most ~3 fields per step,
 * and identity/demographic data never sharing a step with credentials. */
function IdentityFields() {
  return (
    <div className="stack">
      <Input label="Full name" name="full_name" required placeholder="e.g. John Banda" />
      <div className="grid grid--2">
        <Input label="Email address" name="email" type="email" required />
        <Input label="Phone number" name="phone" placeholder="+260 97..." />
      </div>
    </div>
  )
}

function NrcField() {
  return <Input label="National ID (NRC)" name="national_id" placeholder="123456/78/1" />
}

function RegionDistrictFields({
  provinces, regionId, districtId, onRegionChange, onDistrictChange, required,
}: {
  provinces: Province[]; regionId: string; districtId: string
  onRegionChange: (v: string) => void; onDistrictChange: (v: string) => void; required?: boolean
}) {
  const districts = useDistricts(regionId)
  return (
    <div className="grid grid--2">
      <Select
        label={required ? 'Region' : 'Assigned province'} required={required}
        value={regionId} onChange={(e) => onRegionChange(e.target.value)}
      >
        <option value="">{required ? 'Select region…' : 'None / National'}</option>
        {provinces.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </Select>
      <Select
        label={required ? 'District' : 'Assigned district'} required={required}
        value={districtId} onChange={(e) => onDistrictChange(e.target.value)} disabled={!regionId}
      >
        <option value="">{required ? 'Select district…' : 'None'}</option>
        {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
      </Select>
    </div>
  )
}

function submitError(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.message : fallback
}

/* ── Officer tab -> POST /admin/users/create-gov ────────────────────── */

const GOV_TITLES = [
  'Director of Mines', 'Deputy Director of Mines', 'Minister of Mines', 'Permanent Secretary',
  'Chief Mining Engineer', 'Provincial Mining Officer', 'District Mining Officer',
  'Senior Inspector of Mines', 'Inspector of Mines', 'Assistant Inspector',
  'Environmental Officer', 'Geological Survey Officer', 'Mining Cadastre Officer', 'Compliance Officer',
]
const DEPARTMENTS = [
  'Ministry of Mines and Minerals Development', 'Mines Safety Department',
  'Geological Survey Department', 'Mining Cadastre Department',
  'Environmental Management', 'Provincial Administration', 'District Administration',
]
const ALL_GOV_ROLE_OPTIONS = [
  { value: 'super_admin', label: 'Platform Admin' },
  { value: 'gov_director', label: 'Director of Mines' },
  { value: 'gov_regional', label: 'Regional Mining Officer' },
  { value: 'gov_inspector', label: 'District Inspector' },
]

function OfficerForm({ provinces, onCreated }: { provinces: Province[]; onCreated: () => void }) {
  const { user } = useAuth()
  const [regionId, setRegionId] = useState('')
  const [districtId, setDistrictId] = useState('')
  const [govTitleChoice, setGovTitleChoice] = useState('')
  const [govTitleOther, setGovTitleOther] = useState('')

  // Mirrors create_gov_user.html's role dropdown, which is conditioned on
  // the creator's own role (super_admin sees every option; gov_director
  // sees only the two it may create). In practice every user who reaches
  // this React admin portal is super_admin - roles.ts:portalFor() routes
  // every gov_* role to the separate /gov portal instead - but this keeps
  // the same gate live rather than assuming that never changes.
  const roleOptions = user?.role === 'gov_director'
    ? ALL_GOV_ROLE_OPTIONS.filter((o) => o.value === 'gov_regional' || o.value === 'gov_inspector')
    : ALL_GOV_ROLE_OPTIONS

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<CreateResult>('/admin/users/create-gov', body),
    onSuccess: onCreated,
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    create.mutate({
      full_name: String(form.get('full_name') ?? '').trim(),
      email: String(form.get('email') ?? '').trim(),
      phone: String(form.get('phone') ?? '').trim(),
      national_id: String(form.get('national_id') ?? '').trim(),
      password: String(form.get('password') ?? ''),
      role: String(form.get('role') ?? ''),
      gov_title: (govTitleChoice === 'other' ? govTitleOther : govTitleChoice).trim(),
      department: String(form.get('department') ?? ''),
      region_id: regionId,
      district_id: districtId,
    })
  }

  return (
    <form onSubmit={onSubmit} className="stack">
      {create.isError && <Alert tone="danger">{submitError(create.error, 'Could not create that account.')}</Alert>}

      <Wizard
        steps={[
          { title: 'Contact', content: <IdentityFields /> },
          { title: 'Personal details', content: <NrcField /> },
          { title: 'Credentials', content: (
      <PasswordInput
        label="Password" name="password" required minLength={6}
        hint="Min 6 characters. They can change it later via the login page."
      />
          ) },
          { title: 'Role & department', content: (
            <div className="stack">
      <Select label="System role" name="role" required defaultValue="">
        <option value="" disabled>Select a system role...</option>
        {roleOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Select>

      <div>
        {/* Jinja's label reads "Official Title / Position *" but neither the
            markup nor the server route (create_gov_user()) actually require
            it - only email/full_name/password are validated - so this stays
            optional here too, matching real behaviour over the (inaccurate)
            asterisk. */}
        <Select
          label="Official title / position"
          value={govTitleChoice} onChange={(e) => setGovTitleChoice(e.target.value)}
        >
          <option value="">Select title...</option>
          {GOV_TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
          <option value="other">Other (specify)</option>
        </Select>
        {govTitleChoice === 'other' && (
          <div style={{ marginTop: 'var(--sp-2)' }}>
            <Input
              placeholder="Enter official title"
              value={govTitleOther} onChange={(e) => setGovTitleOther(e.target.value)}
            />
          </div>
        )}
      </div>

      <Select label="Department" name="department" defaultValue="">
        <option value="">Select department...</option>
        {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
      </Select>
            </div>
          ) },
          { title: 'Posting area', content: (
      <RegionDistrictFields
        provinces={provinces} regionId={regionId} districtId={districtId}
        onRegionChange={(v) => { setRegionId(v); setDistrictId('') }} onDistrictChange={setDistrictId}
      />
          ) },
        ]}
        footer={(
      <div className="row">
        <Button type="submit" loading={create.isPending}>Create government official</Button>
      </div>
        )}
      />
    </form>
  )
}

/* ── Team Leader tab -> POST /admin/users/create-leader ─────────────── */

function LeaderForm({
  provinces, orgs, onCreated,
}: { provinces: Province[]; orgs: Organization[]; onCreated: () => void }) {
  const [regionId, setRegionId] = useState('')
  const [districtId, setDistrictId] = useState('')

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<CreateResult>('/admin/users/create-leader', body),
    onSuccess: onCreated,
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    create.mutate({
      full_name: String(form.get('full_name') ?? '').trim(),
      email: String(form.get('email') ?? '').trim(),
      phone: String(form.get('phone') ?? '').trim(),
      national_id: String(form.get('national_id') ?? '').trim(),
      password: String(form.get('password') ?? ''),
      org_id: String(form.get('org_id') ?? ''),
      region_id: regionId,
      district_id: districtId,
    })
  }

  return (
    <form onSubmit={onSubmit} className="stack">
      {create.isError && <Alert tone="danger">{submitError(create.error, 'Could not create that account.')}</Alert>}

      <Wizard
        steps={[
          { title: 'Contact', content: <IdentityFields /> },
          { title: 'Personal details', content: <NrcField /> },
          { title: 'Credentials', content: (
      <PasswordInput
        label="Password" name="password" required minLength={6}
        hint="Min 6 characters. They can change it later via the login page."
      />
          ) },
          { title: 'Assignment', content: (
            <div className="stack">
      <Select label="Organisation" name="org_id" defaultValue="">
        <option value="">No organisation (assign later)</option>
        {orgs.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.type})</option>)}
      </Select>

      <RegionDistrictFields
        provinces={provinces} regionId={regionId} districtId={districtId}
        onRegionChange={(v) => { setRegionId(v); setDistrictId('') }} onDistrictChange={setDistrictId}
      />
            </div>
          ) },
        ]}
        footer={(
      <div className="row">
        <Button type="submit" loading={create.isPending}>Create team leader</Button>
      </div>
        )}
      />
    </form>
  )
}

/* ── Miner tab -> POST /admin/users/create-miner ────────────────────── */

function MinerForm({
  provinces, orgs, onCreated,
}: { provinces: Province[]; orgs: Organization[]; onCreated: () => void }) {
  const [regionId, setRegionId] = useState('')
  const [districtId, setDistrictId] = useState('')

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<CreateResult>('/admin/users/create-miner', body),
    onSuccess: onCreated,
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    create.mutate({
      full_name: String(form.get('full_name') ?? '').trim(),
      email: String(form.get('email') ?? '').trim(),
      phone: String(form.get('phone') ?? '').trim(),
      national_id: String(form.get('national_id') ?? '').trim(),
      password: String(form.get('password') ?? ''),
      dob: String(form.get('dob') ?? ''),
      gender: String(form.get('gender') ?? ''),
      org_id: String(form.get('org_id') ?? ''),
      region_id: regionId,
      district_id: districtId,
    })
  }

  return (
    <form onSubmit={onSubmit} className="stack">
      {create.isError && <Alert tone="danger">{submitError(create.error, 'Could not create that account.')}</Alert>}

      <Wizard
        steps={[
          { title: 'Contact', content: <IdentityFields /> },
          { title: 'Personal details', content: (
            <div className="stack">
      <NrcField />
      <div className="grid grid--2">
        <Input label="Date of birth" name="dob" type="date" />
        <Select label="Gender" name="gender" defaultValue="">
          <option value="">Select...</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Other</option>
        </Select>
      </div>
            </div>
          ) },
          { title: 'Credentials', content: (
      <PasswordInput
        label="Password" name="password" required minLength={6}
        hint="Min 6 characters. They can change it later via the login page."
      />
          ) },
          { title: 'Cooperative & area', content: (
            <div className="stack">
      <RegionDistrictFields
        provinces={provinces} regionId={regionId} districtId={districtId}
        onRegionChange={(v) => { setRegionId(v); setDistrictId('') }} onDistrictChange={setDistrictId}
      />

      <div>
        <Select label="Organisation" name="org_id" required defaultValue="">
          <option value="" disabled>Select organisation...</option>
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.type})</option>)}
        </Select>
        <p className="field__hint" style={{ marginTop: 'var(--sp-1)' }}>
          Every miner belongs to a company or cooperative - there is no independent/unaffiliated account type.
        </p>
      </div>
            </div>
          ) },
        ]}
        footer={(
      <div className="row">
        <Button type="submit" loading={create.isPending}>Create miner</Button>
      </div>
        )}
      />
    </form>
  )
}

/* ── Staff tab -> POST /admin/users/create-staff ────────────────────── */

function StaffForm({
  provinces, orgs, onCreated,
}: { provinces: Province[]; orgs: Organization[]; onCreated: () => void }) {
  const [role, setRole] = useState('')
  const [regionId, setRegionId] = useState('')
  const [districtId, setDistrictId] = useState('')
  const selectedRole = SYSTEM_ROLES.find((r) => r.key === role)

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<CreateResult>('/admin/users/create-staff', body),
    onSuccess: onCreated,
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    create.mutate({
      role,
      full_name: String(form.get('full_name') ?? '').trim(),
      email: String(form.get('email') ?? '').trim(),
      phone: String(form.get('phone') ?? '').trim(),
      national_id: String(form.get('national_id') ?? '').trim(),
      password: String(form.get('password') ?? ''),
      org_id: String(form.get('org_id') ?? ''),
      region_id: regionId,
      district_id: districtId,
    })
  }

  return (
    <form onSubmit={onSubmit} className="stack">
      {create.isError && <Alert tone="danger">{submitError(create.error, 'Could not create that account.')}</Alert>}

      <Wizard
        steps={[
          { title: 'Role', content: (
      <div>
        {/* SYSTEM_ROLES is a hardcoded mirror of the backend's role catalog -
            see lib/systemRoles.ts for why, and its known gap (custom roles
            created via the Role Builder won't appear here). */}
        <Select label="Role" required value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">Select a role...</option>
          {SYSTEM_ROLES.map((r) => <option key={r.key} value={r.key}>{r.title} — {r.tier}</option>)}
        </Select>
        {selectedRole && <p className="field__hint" style={{ marginTop: 'var(--sp-1)' }}>{selectedRole.description}</p>}
      </div>
          ) },
          { title: 'Contact', content: <IdentityFields /> },
          { title: 'Personal details', content: <NrcField /> },
          { title: 'Credentials', content: (
      <PasswordInput label="Password" name="password" required minLength={6} hint="They can change it later via the login page." />
          ) },
          { title: 'Assignment', content: (
            <div className="stack">
      <div>
        <Select label="Organisation" name="org_id" defaultValue="">
          <option value="">No organisation (assign later)</option>
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.type})</option>)}
        </Select>
        <p className="field__hint" style={{ marginTop: 'var(--sp-1)' }}>
          Required for org-scoped roles (Safety Officer, Site Manager, Miner...) so they see their organisation's data.
        </p>
      </div>

      <RegionDistrictFields
        provinces={provinces} regionId={regionId} districtId={districtId}
        onRegionChange={(v) => { setRegionId(v); setDistrictId('') }} onDistrictChange={setDistrictId}
      />
            </div>
          ) },
        ]}
        footer={(
      <div className="row">
        <Button type="submit" loading={create.isPending}>Create account</Button>
      </div>
        )}
      />
    </form>
  )
}
